import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const newSessionId = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`).replace(/-/g, "");

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I'm Krishna, the A2Z Rankings assistant. How can I help you today — a project, a question about our services, or a free discovery call?",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionRef = useRef(newSessionId());
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const r = await api.post("/chat", { session_id: sessionRef.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry, I'm having trouble right now. Please try the contact page or email info@a2zrankings.com." },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating toggle */}
      <button
        type="button"
        aria-label={open ? "Close chat" : "Open chat"}
        data-testid="chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[90] flex h-14 w-14 items-center justify-center rounded-full bg-brand-emerald text-brand-black shadow-lg shadow-brand-emerald/30 transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-6 z-[90] flex h-[32rem] w-[22rem] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border border-brand-mist/20 bg-brand-charcoal shadow-2xl"
        >
          <div className="flex items-center gap-3 border-b border-brand-mist/15 bg-brand-black/60 px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-emerald/20 font-display text-sm font-bold text-brand-emerald">
              K
            </div>
            <div>
              <p className="text-sm font-semibold text-brand-snow">Krishna</p>
              <p className="text-xs text-brand-mist">A2Z Rankings assistant</p>
            </div>
          </div>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={
                    "max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed " +
                    (m.role === "user"
                      ? "rounded-br-sm bg-brand-emerald text-brand-black"
                      : "rounded-bl-sm bg-brand-black/60 text-brand-snow")
                  }
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-brand-black/60 px-3.5 py-2.5 text-sm text-brand-mist">
                  <Loader2 className="h-4 w-4 animate-spin" /> Typing…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="flex items-center gap-2 border-t border-brand-mist/15 bg-brand-black/60 p-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about our services…"
              data-testid="chat-input"
              className="flex-1 rounded-full border border-brand-mist/20 bg-brand-charcoal px-4 py-2.5 text-sm text-brand-snow placeholder:text-brand-mist/60 focus:border-brand-emerald focus:outline-none"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="chat-send"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-emerald text-brand-black transition-opacity disabled:opacity-40"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
